'use client';

import Script from 'next/script';
import {useEffect, useRef} from 'react';

declare global {
  interface Window extends HTMLIFrameElement {
    openbigwin: () => void;
    closebigwin: () => void;
  }
}

/** Returns the Goftino's `iframe` element. */
async function getGoftinoIframe() {
  let goftino: HTMLIFrameElement | undefined;

  // Waiting until the `iframe` is loaded
  while (!goftino?.contentDocument) {
    goftino = document.getElementById('goftino_w') as HTMLIFrameElement;
    await new Promise(resolve => setTimeout(resolve, 10));
  }

  return goftino;
}

/** Hides the default Goftino's button element. */
async function hideGoftinoButton() {
  const goftino = await getGoftinoIframe();

  // Should be specified, otherwise default
  // button's background color would be white
  goftino.style.colorScheme = 'light';

  // The element is on top of all the other elements
  // and is preventing user interactions. Moving the
  // element outside of the viewport.
  goftino.style.right = '-100%';
  goftino.style.userSelect = 'none';

  if (goftino.contentDocument) {
    const box =
      goftino.contentDocument.querySelector<HTMLElement>('#box-widget-icon');
    if (box) {
      goftino.contentDocument.body.removeChild(box);
    }
  }
}

/** Opens the Goftino's chat interface. */
export async function openGoftino() {
  const goftinoWindow = (await getGoftinoIframe()).contentWindow;
  if (goftinoWindow) {
    goftinoWindow.openbigwin();

    // Monkey-patching the original close handler
    // to undo re-styling the `iframe` position
    const originalHandler = goftinoWindow.closebigwin;
    goftinoWindow.closebigwin = async () => {
      originalHandler();
      await hideGoftinoButton();

      goftinoWindow.closebigwin = originalHandler;
    };
  }
}

export default function Goftino() {
  const timerId = useRef<number>(null);

  useEffect(() => () => window.clearInterval(timerId.current!));

  function onReady() {
    hideGoftinoButton();

    // Should be run periodically because the default
    // Goftino's button reappear on occasion
    timerId.current = window.setInterval(hideGoftinoButton, 1e4);
  }

  return (
    <Script
      onReady={onReady}
      id="goftino-widget"
      strategy="lazyOnload"
      dangerouslySetInnerHTML={{
        __html:
          '!function(){var i="Tsn1eH",a=window,d=document;function g(){var g=d.createElement("script"),s="https://www.goftino.com/widget/"+i,l=localStorage.getItem("goftino_"+i);g.async=!0,g.src=l?s+"?o="+l:s;d.getElementsByTagName("head")[0].appendChild(g);}"complete"===d.readyState?g():a.attachEvent?a.attachEvent("onload",g):a.addEventListener("load",g,!1);}();'
      }}
    />
  );
}
